/*
 * Post login redirect service
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2026 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  Params,
  Router
} from '@angular/router';
import { Constants } from 'app/shared/config/constants';
import { AuthService } from 'app/shared/services/auth.service';
import { UserService } from 'app/shared/services/user.service';
import { UiConfigService } from 'app/shared/services/ui-config.service';
import { ContactEmailService } from 'app/shared/services/contact-email.service';
import { TokenService } from 'app/shared/services/token.service';

export interface GbruSnapshot {
  path: string[];
  qParams: Params;
  user: string;
  [ key: string ]: any;
}

export interface PostLoginRedirectContext {
  gbru?: GbruSnapshot | null;
  redirectPath?: string | null;
  returnUrl?: string | null;
  authRedirectApp?: string | null;
  handOffToken?: string | null;
  credentialsLogin?: boolean;
}

@Injectable ( {
  providedIn: 'root',
} )
export class PostLoginRedirectService {

  constructor (
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private uiConfigService: UiConfigService,
    private contactEmailService: ContactEmailService,
    private tokenService: TokenService,
  ) {
  }

  public parseGbru ( rawGbru: string | null ): GbruSnapshot | null {
    if ( !rawGbru ) {
      return null;
    }

    try {
      const gbru = JSON.parse ( atob ( rawGbru ) );
      return Array.isArray ( gbru?.path ) && typeof gbru.user === 'string' ? gbru as GbruSnapshot : null;
    } catch ( e ) {
      return null;
    }
  }

  // Resolves false only when a hand-off could not be checked for a valid contact email, so the caller can let the user retry
  public redirect ( context: PostLoginRedirectContext ): Promise<boolean> {
    // The requested app is kept until the hand-off email check passes, so a retry or a log out still returns to that app
    if ( context.credentialsLogin && context.handOffToken ) {
      return this.redirectAfterHandOffEmailCheck ( context );
    }

    const gbru = context.gbru;
    sessionStorage.removeItem ( Constants.authRedirectAppStorageKey );

    if ( this.isContactEmailUpdateRequired ( context ) ) {
      const { credentialsLogin, ...pendingContext } = context;
      this.showContactEmailUpdate ( this.userService.currentUser.userId, pendingContext );
    } else if ( context.authRedirectApp && context.handOffToken ) {
      this.userService.handOffToApp ( context.handOffToken, context.authRedirectApp );
    } else if ( context.authRedirectApp === 'REPORTER' && ( this.userService.userHasPermission ( 'ADMIN_USER' ) || this.userService.userHasPermission ( 'LIMITED_ADMIN' ) ) ) {
      this.userService.loginAdmin ( this.authService.accessToken, true );
    } else if ( gbru && this.userService.currentUser?.userId === gbru.user ) {
      const previousReporterCurrentClient = gbru[ Constants.localStorageKeys.reporterCurrentClient ];

      this.userService.enableClientChange ();
      this.userService.updateUserAccessibility ( gbru[ Constants.localStorageKeys.reporterUser ].clientAccessibility );
      this.userService.currentClient = previousReporterCurrentClient;
      this.userService.currentClientNumber = previousReporterCurrentClient.clientNumber;
      const previousBuSettings = gbru[ Constants.localStorageKeys.reporterBusinessUnitsSettings ];
      if ( previousBuSettings ) {
        localStorage.setItem ( Constants.localStorageKeys.reporterBusinessUnitsSettings, JSON.stringify ( previousBuSettings ) );
      }
      this.userService.disableClientChange ();
      this.router.navigate ( gbru.path, {
        queryParams: gbru.qParams,
        replaceUrl: true,
      } ).then ();
    } else if ( context.redirectPath ) {
      this.router.navigateByUrl ( context.redirectPath, {
        replaceUrl: true,
      } ).then ();
    } else if ( this.isSafeReturnUrl ( context.returnUrl ) ) {
      this.router.navigateByUrl ( context.returnUrl, { replaceUrl: true } )
        .catch ( () => this.navigateToHome () );
    } else {
      this.navigateToHome ();
    }

    return Promise.resolve ( true );
  }

  // The hand-off token belongs to another app, so Reporter has no user loaded and the email is fetched with that token.
  // The hand-off never continues without a successful check, so the user can retry.
  private redirectAfterHandOffEmailCheck ( context: PostLoginRedirectContext ): Promise<boolean> {
    const { credentialsLogin, ...pendingContext } = context;
    const userId = this.tokenService.decode ( context.handOffToken )?.sub;

    if ( !userId ) {
      return Promise.resolve ( false );
    }

    return new Promise<boolean> ( resolve => {
      this.contactEmailService.fetchEmail ( userId, context.handOffToken )
        .subscribe ( {
          next: email => {
            if ( this.contactEmailService.isInvalid ( email ) ) {
              this.showContactEmailUpdate ( userId, pendingContext );
              resolve ( true );
            } else {
              resolve ( this.redirect ( pendingContext ) );
            }
          },
          error: () => resolve ( false ),
        } );
    } );
  }

  private showContactEmailUpdate ( userId: string, pendingContext: PostLoginRedirectContext ): void {
    this.contactEmailService.requireUpdate ( userId, pendingContext );
    this.router.navigate ( [ '/update-email' ], { replaceUrl: true } ).then ();
  }

  private isContactEmailUpdateRequired ( context: PostLoginRedirectContext ): boolean {
    return !!context.credentialsLogin &&
      this.contactEmailService.isInvalid ( this.userService.currentUser?.email );
  }

  private navigateToHome (): void {
    this.router.navigate ( [ `/home${ this.uiConfigService.showWelcomePage ? '/welcome' : '' }` ], {
      replaceUrl: true,
      state: { showWelcomePage: this.uiConfigService.showWelcomePage },
    } );
  }

  private isSafeReturnUrl ( url?: string | null ): url is string {
    const trimmedUrl = url?.trim ();

    return !!trimmedUrl &&
      trimmedUrl.startsWith ( '/' ) &&
      !trimmedUrl.startsWith ( '//' ) &&
      !/^\/(?:login|authenticate|auth\/mfa\/callback|ping-preauth|fed-auth|recover)(?:[/?#;]|$)/.test ( trimmedUrl ) &&
      !/^\/[^/?#;]+\/login(?:[/?#;]|$)/.test ( trimmedUrl );
  }
}
